import { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { subscriptionsAPI } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Check,
  X,
  Star,
  Zap,
  Crown,
  Building2,
} from 'lucide-react';

const planIcons = {
  free: Star,
  starter: Zap,
  professional: Crown,
  enterprise: Building2,
};

const planColors = {
  free: 'border-slate-200',
  starter: 'border-blue-200',
  professional: 'border-emerald-300 ring-2 ring-emerald-100',
  enterprise: 'border-purple-200',
};

export const SubscriptionsPage = () => {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  const isSuperAdmin = user?.is_super_admin;

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await subscriptionsAPI.listPlans();
      setPlans(response.data);
    } catch (err) {
      toast.error('Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const featureLabels = {
    patients: 'Patient Management',
    appointments: 'Appointment Scheduling',
    basic_emr: 'Basic EMR',
    billing: 'Billing & Invoicing',
    inventory: 'Inventory Management',
    beds: 'Bed Management',
    advanced_reports: 'Advanced Reports & Analytics',
    multi_branch: 'Multi-Branch Support',
    api_access: 'API Access',
  };

  if (!isSuperAdmin) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Access denied. Super admin only.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-96" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="subscriptions-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Subscription Plans
        </h1>
        <p className="mt-1 text-slate-600">
          Manage subscription plans and feature gating for hospitals
        </p>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {plans.map((plan, index) => {
          const PlanIcon = planIcons[plan.code] || Star;
          const isRecommended = plan.code === 'professional';
          
          return (
            <Card
              key={plan.id}
              className={`relative plan-card animate-fade-in ${planColors[plan.code]} ${
                isRecommended ? 'recommended' : ''
              }`}
              style={{ animationDelay: `${index * 0.1}s` }}
              data-testid={`plan-card-${plan.code}`}
            >
              {isRecommended && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-emerald-600 text-white">Recommended</Badge>
                </div>
              )}
              <CardHeader className="text-center pt-8">
                <div className="mx-auto mb-4 p-3 rounded-full bg-emerald-100 w-fit">
                  <PlanIcon className="h-6 w-6 text-emerald-600" />
                </div>
                <CardTitle className="text-xl">{plan.name}</CardTitle>
                <CardDescription>
                  <span className="text-3xl font-bold text-slate-900">
                    ₹{plan.price.toLocaleString()}
                  </span>
                  <span className="text-slate-500">/{plan.billing_cycle}</span>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Limits */}
                  <div className="space-y-2 pb-4 border-b border-slate-100">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Max Users</span>
                      <span className="font-medium text-slate-900">
                        {plan.max_users === -1 ? 'Unlimited' : plan.max_users}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Max Branches</span>
                      <span className="font-medium text-slate-900">
                        {plan.max_branches === -1 ? 'Unlimited' : plan.max_branches}
                      </span>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="space-y-2">
                    {Object.entries(plan.features || {}).map(([feature, enabled]) => (
                      <div
                        key={feature}
                        className={`flex items-center gap-2 text-sm ${
                          enabled ? 'text-slate-700' : 'text-slate-400'
                        }`}
                      >
                        {enabled ? (
                          <Check className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                        ) : (
                          <X className="h-4 w-4 text-slate-300 flex-shrink-0" />
                        )}
                        <span className={enabled ? '' : 'line-through'}>
                          {featureLabels[feature] || feature}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Status */}
                  <div className="pt-4">
                    <Badge className={plan.is_active ? 'bg-emerald-100 text-emerald-700 w-full justify-center' : 'bg-slate-100 text-slate-600 w-full justify-center'}>
                      {plan.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Feature Matrix */}
      <Card className="animate-fade-in" style={{ animationDelay: '0.5s' }}>
        <CardHeader>
          <CardTitle>Feature Comparison Matrix</CardTitle>
          <CardDescription>Compare features across all subscription plans</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">Feature</th>
                  {plans.map((plan) => (
                    <th key={plan.id} className="text-center py-3 px-4 font-semibold text-slate-700">
                      {plan.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.keys(featureLabels).map((feature) => (
                  <tr key={feature} className="border-b border-slate-100">
                    <td className="py-3 px-4 text-slate-600">{featureLabels[feature]}</td>
                    {plans.map((plan) => (
                      <td key={plan.id} className="text-center py-3 px-4">
                        {plan.features?.[feature] ? (
                          <Check className="h-5 w-5 text-emerald-600 mx-auto" />
                        ) : (
                          <X className="h-5 w-5 text-slate-300 mx-auto" />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
                <tr className="border-b border-slate-100">
                  <td className="py-3 px-4 text-slate-600 font-medium">Max Users</td>
                  {plans.map((plan) => (
                    <td key={plan.id} className="text-center py-3 px-4 font-medium">
                      {plan.max_users === -1 ? '∞' : plan.max_users}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="py-3 px-4 text-slate-600 font-medium">Max Branches</td>
                  {plans.map((plan) => (
                    <td key={plan.id} className="text-center py-3 px-4 font-medium">
                      {plan.max_branches === -1 ? '∞' : plan.max_branches}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionsPage;
